import React from "react";
import { Table } from "../../../../../../components/table";
import type { ColumnsType } from "antd/es/table";
import type { FormatConfig } from "./FormatButtons";

interface SeriesData {
  name: string;
  ticker_symbol: string;
  data: string[];
  metricGraphType: string;
}

interface StatisticsApiResponse {
  series: SeriesData[];
  categories: string[];
}

interface TableRow {
  key: string;
  lineItem: string;
  isSpacer?: boolean;
  [key: string]: string | number | boolean | undefined;
}

interface StatisticsTableProps {
  data?: StatisticsApiResponse;
  loading?: boolean;
  formatConfig?: FormatConfig;
}

const METRIC_GROUPS: string[][] = [
  ["Total Number Of Issued Shares"],
  ["Price to Earnings Ratio", "Price to Book Ratio"],
  [
    "EBITDA Margin %",
    "Return on Asset",
    "Return on Equity",
    "Gross Profit Margin %",
    "Net Profit Margin %",
  ],
  ["Quick Ratio", "Current Assets Ratio"],
  ["Revenue per Share", "Free Cash Flow per Share"],
];

export const StatisticsTable: React.FC<StatisticsTableProps> = ({
  data,
  loading,
  formatConfig = { decimal: "", scale: "millions" },
}) => {
  // Only counts are scaled; ratios and per-share values never are.
  const SCALED_METRICS = ["Total Number Of Issued Shares"];

  const SCALES: { [key: string]: { divisor: number; suffix: string } } = {
    thousands: { divisor: 1000, suffix: "K" },
    millions: { divisor: 1000000, suffix: "M" },
    billions: { divisor: 1000000000, suffix: "B" },
  };

  const DECIMAL_PLACES: { [key: string]: number } = {
    decimal_0: 1,
    decimal_00: 2,
  };

  const formatValue = (rawValue: string, metricName: string): string => {
    // Percentage metrics arrive as e.g. "19.8302%".
    const value = String(rawValue).trim();
    const isPercent = value.endsWith("%");
    const numericPart = isPercent ? value.slice(0, -1) : value;
    const numericValue = Number(numericPart);

    // Leave anything non-numeric (e.g. "-") as the API sent it.
    if (numericPart === "" || isNaN(numericValue)) {
      return rawValue;
    }

    const decimalPlaces = DECIMAL_PLACES[formatConfig.decimal];

    if (!SCALED_METRICS.includes(metricName)) {
      if (decimalPlaces === undefined) {
        return rawValue;
      }
      const formatted = numericValue.toLocaleString("en-US", {
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
      });
      return isPercent ? `${formatted}%` : formatted;
    }

    const scaleConfig = SCALES[formatConfig.scale];
    const isNegative = numericValue < 0;
    const scaledValue = scaleConfig
      ? Math.abs(numericValue) / scaleConfig.divisor
      : Math.abs(numericValue);

    // Unscaled share counts are whole numbers; scaled ones default to 2dp.
    const places = decimalPlaces ?? (scaleConfig ? 2 : 0);
    const formattedNumber = scaledValue.toLocaleString("en-US", {
      minimumFractionDigits: places,
      maximumFractionDigits: places,
    });

    const valueWithSuffix = `${formattedNumber}${scaleConfig?.suffix ?? ""}`;

    return isNegative ? `(${valueWithSuffix})` : valueWithSuffix;
  };

  const transformDataToTable = (): {
    columns: ColumnsType<TableRow>;
    dataSource: TableRow[];
  } => {
    if (!data?.series || !data?.categories) {
      return { columns: [], dataSource: [] };
    }

    const { series, categories } = data;
    const sortedCategories = [...categories].reverse();

    const columns: ColumnsType<TableRow> = [
      {
        title: "Financial Year",
        dataIndex: "lineItem",
        key: "lineItem",
        fixed: "left",
        width: 220,
        className: "text-sm",
        render: (text: string, record: TableRow) =>
          record.isSpacer ? "" : text,
        onCell: (record: TableRow) => {
          const styles: React.CSSProperties = {};
          if (record.isSpacer) {
            styles.height = 16;
            styles.padding = 0;
            styles.border = "none";
          }
          return { style: styles };
        },
      },
      ...sortedCategories.map((year, index) => ({
        // Newest year comes first and isn't a full fiscal year yet.
        title: index === 0 ? `${year} (LTM)` : year,
        dataIndex: `year_${year}`,
        key: `year_${year}`,
        align: "right" as const,
        width: 120,
        className: "text-sm",
        render: (value: string, record: TableRow) => {
          if (record.isSpacer === true) return "";
          return value ? value : "-";
        },
        onCell: (record: TableRow) => {
          const styles: React.CSSProperties = {};
          if (record.isSpacer) {
            styles.height = 16;
            styles.padding = 0;
            styles.border = "none";
          }
          return { style: styles };
        },
      })),
    ];

    const seriesByName = new Map<string, SeriesData>();
    series.forEach((s) => {
      const metricName = s.name.split(":")[0].trim();
      if (!seriesByName.has(metricName)) {
        seriesByName.set(metricName, s);
      }
    });

    const buildRow = (s: SeriesData, rowIndex: number): TableRow => {
      const metricName = s.name.split(":")[0].trim();
      const row: TableRow = {
        key: `row_${rowIndex}`,
        lineItem: metricName,
      };

      sortedCategories.forEach((year) => {
        const originalIndex = categories.indexOf(year);
        const rawValue = s.data[originalIndex];
        row[`year_${year}`] =
          rawValue !== undefined && rawValue !== null
            ? formatValue(rawValue, metricName)
            : "-";
      });

      return row;
    };

    const buildSpacerRow = (rowIndex: number): TableRow => {
      const row: TableRow = {
        key: `spacer_${rowIndex}`,
        lineItem: "",
        isSpacer: true,
      };
      sortedCategories.forEach((year) => {
        row[`year_${year}`] = "";
      });
      return row;
    };

    const dataSource: TableRow[] = [];
    let rowIndex = 0;

    METRIC_GROUPS.forEach((group, groupIndex) => {
      group.forEach((metricName) => {
        const s = seriesByName.get(metricName);
        if (s) {
          dataSource.push(buildRow(s, rowIndex));
          rowIndex++;
        }
      });

      if (groupIndex < METRIC_GROUPS.length - 1) {
        dataSource.push(buildSpacerRow(rowIndex));
        rowIndex++;
      }
    });

    return { columns, dataSource };
  };

  const { columns, dataSource } = transformDataToTable();

  return (
    <div className="w-full">
      <Table<TableRow>
        columns={columns}
        dataSource={dataSource}
        rowKey="key"
        rowClassName={(record) => (record.isSpacer ? "vi-stat-spacer-row" : "")}
        pagination={false}
        size="small"
        bordered={false}
        scroll={{ x: Math.max(600, columns.length * 120) }}
        className="financial-table"
        showHeader={true}
        loading={loading}
      />
    </div>
  );
};
